import {AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, HostListener, Inject, OnInit, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {getLanguageEnumByLanguageCode, LanguageEnum} from '@core/enums/language.enum';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {IndicationModel} from '@core/modules/rest/indication/response/indication.model';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {IndicationLanguageInfoModel} from '@core/modules/rest/indication/response/indication-language-info.model';
import {MedicalFieldEnum} from '@core/modules/rest/indication/enums/medical-field.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'app-new-indication-popup',
    templateUrl: './new-indication-popup.component.html',
    styleUrls: ['./new-indication-popup.component.scss']
})
export class NewIndicationPopupComponent implements OnInit, AfterViewChecked {
    @Output() createIndicationEvent = new EventEmitter<IndicationModel>();
    title: string;
    fieldOptions = [];
    selectedHealthcareField: any;
    appLanguageLabel: string;

    form: FormGroup;
    healthcareFieldFormControl = new FormControl('', [Validators.required]);
    titleMandatoryFormControl = new FormControl('', [Validators.required]);
    descriptionMandatoryFormControl: FormControl = new FormControl('', [Validators.required]);

    selectLanguageControl = new FormControl('');

    titleEnglishExist: boolean;
    titleGermanExist: boolean;
    titleSerbianExist: boolean;
    titleMandatoryExist: boolean;
    titleMandatoryMissing: boolean;
    titleEnglishMissing: boolean;
    titleGermanMissing: boolean;
    titleSerbianMissing: boolean;
    descriptionMandatoryMissing: boolean;
    descriptionEnglishMissing: boolean;
    descriptionGermanMissing: boolean;
    descriptionSerbianMissing: boolean;
    languageOptions = [];
    selectedLanguage: any;
    innerWidth: any;

    info: IndicationLanguageInfoModel[] = [];
    appLanguage: LanguageEnum;

    showSelectedLanguageFields = false;
    showAddAnotherLanguageButton = true;
    indication: IndicationModel;
    actionButtonText: string;

    @HostListener('window:resize', ['$event'])
    onResize(): void {
        this.innerWidth = window.innerWidth;
    }

    constructor(private readonly changeDetectorRef: ChangeDetectorRef, private dialog: MatDialogRef<NewIndicationPopupComponent>,
                @Inject(MAT_DIALOG_DATA) data: IndicationModel, private translate: TranslateService, private fb: FormBuilder) {
        this.onResize();
        this.indication = data;
        this.actionButtonText = this.indication ? this.translate.instant('GENERAL.EDIT') : this.translate.instant('GENERAL.CREATE');
        this.fieldOptions.push({
            selectId: MedicalFieldEnum.OPHTHALMOLOGY,
            selectValue: this.translate.instant('GENERAL.OPHTHALMOLOGY')
        });
    }

    ngOnInit(): void {
        this.title = this.indication ? this.translate.instant('GENERAL.EDIT_EDUCATION') : this.translate.instant('GENERAL.NEW_EDUCATION');
        this.appLanguage = getLanguageEnumByLanguageCode(localStorage.getItem('languageCode'));
        this.addLanguageOptions(this.appLanguage);
        if (this.appLanguage === LanguageEnum.GERMAN) {
            this.appLanguageLabel = this.translate.instant('GENERAL.GERMAN');
        } else if (this.appLanguage === LanguageEnum.SERBIAN) {
            this.appLanguageLabel = this.translate.instant('GENERAL.SERBIAN');
        } else {
            this.appLanguageLabel = this.translate.instant('GENERAL.ENGLISH');
        }

        this.form = this.fb.group({
            healthcareField: this.healthcareFieldFormControl,
            titleMandatory: this.titleMandatoryFormControl,
            descriptionMandatory: this.descriptionMandatoryFormControl,
            selectLanguageControl: this.selectLanguageControl,
            newLanguageFields: this.fb.array([])
        });

        this.form.controls.titleMandatory.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            const val = this.form.controls.titleMandatory.value;
            if (!val) {
                this.titleMandatoryMissing = true;
            }
        });
        this.form.controls.descriptionMandatory.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            const val = this.form.controls.descriptionMandatory.value;
            if (!val) {
                this.descriptionMandatoryMissing = true;
            }
        });
        this.info.push({
            language: this.appLanguage,
            data: {
                title: this.form.controls.titleMandatory.value,
                description: this.form.controls.descriptionMandatory.value
            }
        });

        if (this.indication) {
            this.setFormValuesForIndicationEditing();
        }
    }

    setFormValuesForIndicationEditing(): void {
        const mandatoryInfo = this.indication.info.find(item => item.language === this.appLanguage);
        this.selectedHealthcareField = this.indication.field;
        this.form.get('titleMandatory').setValue(mandatoryInfo.data.title);
        this.form.get('descriptionMandatory').setValue(mandatoryInfo.data.description);
        for (const item of this.indication.info) {
            if (item !== mandatoryInfo) {
                this.newLanguageFields.push(this.fb.group({
                    selectLanguageControl: this.fb.control(item.language, [Validators.required]),
                    titleOther: this.fb.control(item.data.title, [Validators.required]),
                    descriptionOther: this.fb.control(item.data.description, [Validators.required])
                }));
                const language = this.languageOptions.find(lang => lang.selectId === item.language);
                const index = this.languageOptions.indexOf(language);
                this.languageOptions.splice(index, 1);
            }
        }
        this.showAddAnotherLanguageButton = this.languageOptions.length > 0;
    }

    private addLanguageOptions(language: LanguageEnum): void {
        this.languageOptions.push({
            selectId: LanguageEnum.ENGLISH_GB,
            selectValue: this.translate.instant('GENERAL.ENGLISH')
        });
        this.languageOptions.push({
            selectId: LanguageEnum.GERMAN,
            selectValue: this.translate.instant('GENERAL.GERMAN')
        });
        this.languageOptions.push({
            selectId: LanguageEnum.SERBIAN,
            selectValue: this.translate.instant('GENERAL.SERBIAN')
        });
        const appLang = this.languageOptions.find(item => item.selectId === language);
        const index = this.languageOptions.indexOf(appLang);
        this.languageOptions.splice(index, 1);
    }

    onSelectFieldChange(event: any): void {
        this.selectedHealthcareField = event;
    }

    onCancel(): void {
        this.dialog.close();
    }

    onCreate(): void {
        this.titleMandatoryMissing = this.titleMandatoryFormControl.invalid;
        this.descriptionMandatoryMissing = this.descriptionMandatoryFormControl.invalid;

        if (this.isFormValid()) {
            this.info = [];
            this.addValuesToInfo();
            const request: IndicationModel = {
                id: this.indication?.id,
                field: this.selectedHealthcareField,
                info: this.info
            };
            this.createIndicationEvent.emit(request);
        }

        this.form.markAllAsTouched();
    }

    isFormValid(): boolean {
        if (this.selectedHealthcareField !== undefined && this.titleMandatoryFormControl.valid && this.descriptionMandatoryFormControl.valid) {
            for (const control of this.newLanguageFields.controls) {
                if (control.invalid) {
                    return false;
                }
            }
            return true;
        }
        return false;
    }

    addValuesToInfo(): void {
        this.setObjectValueInArray(this.appLanguage, this.form.controls.titleMandatory.value, this.form.controls.descriptionMandatory.value);
        for (const control of this.newLanguageFields.controls) {
            if (control.get('selectLanguageControl').value !== '') {
                this.setObjectValueInArray(control.get('selectLanguageControl').value, control.get('titleOther').value, control.get('descriptionOther').value);
            }
        }
    }

    onLanguageChange(language: LanguageEnum): void {
        this.newLanguageFields.at(this.newLanguageFields.length - 1).get('selectLanguageControl').setValue(language);
        const selectedLanguage = this.languageOptions.find(item => item.selectId === language);
        const index = this.languageOptions.indexOf(selectedLanguage);
        this.languageOptions.splice(index, 1);
        if (this.languageOptions.length > 0) {
            this.showAddAnotherLanguageButton = true;
        }
    }

    setObjectValueInArray(language: LanguageEnum, title: string, description: string): void {
        const item = this.info.find(x => x.language === language);
        const itemIndex = this.info.indexOf(item);
        if (itemIndex !== -1) {
            item.data.title = title;
            item.data.description = description;
            this.info.splice(itemIndex, 1, item);
        } else {
            this.info.push({
                language,
                data: {title, description}
            });
        }
    }

    get newLanguageFields(): FormArray {
        return this.form.get('newLanguageFields') as FormArray;
    }

    addNewTranslation(): void {
        this.showSelectedLanguageFields = false;
        this.showAddAnotherLanguageButton = false;
        this.newLanguageFields.push(this.fb.group({
            selectLanguageControl: this.fb.control('', [Validators.required]),
            titleOther: this.fb.control('', [Validators.required]),
            descriptionOther: this.fb.control('', [Validators.required])
        }));
    }

    abstractToFormControl(field: AbstractControl): FormControl {
        return field as FormControl;
    }

    onRemoveFields(index: number): void {
        const langName = this.getLanguageNameByEnum(this.newLanguageFields.at(index).get('selectLanguageControl').value);
        const langEnum = this.newLanguageFields.at(index).get('selectLanguageControl').value;
        this.languageOptions.push({
            selectId: langEnum,
            selectValue: langName
        });
        this.newLanguageFields.removeAt(index);
        if (this.languageOptions.length > 0) {
            this.showAddAnotherLanguageButton = true;
        }
    }

    getLanguageNameByEnum(languageEnum: LanguageEnum): string {
        if (languageEnum === LanguageEnum.GERMAN) {
            return this.translate.instant('GENERAL.GERMAN');
        } else if (languageEnum === LanguageEnum.SERBIAN) {
            return this.translate.instant('GENERAL.SERBIAN');
        } else if (languageEnum === LanguageEnum.ENGLISH_US || languageEnum === LanguageEnum.ENGLISH_GB) {
            return this.translate.instant('GENERAL.ENGLISH');
        }
        return undefined;
    }

    showErrorExistMessage(selectLanguageControl: LanguageEnum): boolean {
        if (selectLanguageControl === LanguageEnum.GERMAN) {
            return this.titleGermanExist;
        } else if (selectLanguageControl === LanguageEnum.SERBIAN) {
            return this.titleSerbianExist;
        } else {
            return this.titleEnglishExist;
        }
    }

    showDescriptionMissingMessage(selectLanguageControl: LanguageEnum): boolean {
        if (selectLanguageControl === LanguageEnum.GERMAN) {
            return this.descriptionGermanMissing;
        } else if (selectLanguageControl === LanguageEnum.SERBIAN) {
            return this.descriptionSerbianMissing;
        } else {
            return this.descriptionEnglishMissing;
        }
    }

    showTitleMissing(selectLanguageControl: LanguageEnum): boolean {
        if (selectLanguageControl === LanguageEnum.GERMAN) {
            return this.titleGermanMissing;
        } else if (selectLanguageControl === LanguageEnum.SERBIAN) {
            return this.titleSerbianMissing;
        } else {
            return this.titleEnglishMissing;
        }
    }

    ngAfterViewChecked(): void {
        this.changeDetectorRef.detectChanges();
    }
}
